import { useState, ReactNode } from 'react';

import { DashboardCard } from '@/components/ui/dashboard-card';
import { EnergyManagementChart } from '@/components/energy-management-chart';
import { DateRangePicker } from '@/components/ui/date-range-picker';

import { cn } from '@/utils/utils';
import { RangeGranularity } from '@/features/dashboard/hooks/queries/get-overview';
import { useGetPlantEnergyManagementChartQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-energy-management-chart';
import { usePermission } from '@/hooks/use-permission';

export const PlantProductionAndStorageEnergyManagement = ({
  className,
  plantId,
  isLoading,
  children,
}: {
  className?: string;
  plantId: string | undefined;
  isLoading: boolean;
  children?: ReactNode;
}) => {
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  }>({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    rangeGranularity: '5min',
  });

  const { data, isFetching } = useGetPlantEnergyManagementChartQuery({
    plantId,
    dateRange,
    enabled: Boolean(plantId),
  });

  const canView1MinuteGranularity = usePermission({
    resource: 'plants',
    action: 'view-1-minute-granularity-energy-management.plant',
  });

  return (
    <DashboardCard
      className={cn(
        `w-full gap-4`,
        isFetching && 'pointer-events-none animate-pulse opacity-60',
        className
      )}
      isLoading={isLoading}
    >
      <>
        <div className="flex flex-col justify-between gap-2 sm:flex-row">
          <h3 className="font-semibold text-nowrap">Energy management</h3>
          <div className="flex justify-between gap-4 sm:justify-start">
            <DateRangePicker
              onUpdate={(values) => {
                setDateRange((current) => ({
                  ...current,
                  startDate: values.range.from,
                  endDate: values.range.to,
                  rangeGranularity: values.rangeGranularity as RangeGranularity,
                }));
              }}
              initialDateFrom={dateRange.startDate}
              initialDateTo={dateRange.endDate}
              locale="ro-RO"
              defaultGranularity="5min"
              hideGranularities={canView1MinuteGranularity ? [] : ['1min']}
            />
            {children}
          </div>
        </div>
        <div className="h-full">
          {data?.length ? (
            <EnergyManagementChart
              data={data ?? []}
              rangeGranularity={dateRange.rangeGranularity}
            />
          ) : isFetching ? (
            <div className="flex h-full min-h-[128px] w-full items-center justify-center rounded-md bg-gray-50"></div>
          ) : (
            <div className="flex h-full min-h-[128px] w-full items-center justify-center rounded-md bg-gray-50">
              <span className="text-muted-foreground">No data found</span>
            </div>
          )}
        </div>
      </>
    </DashboardCard>
  );
};
